import { firebaseService } from './FirebaseService';
import { ref, onValue, set, onDisconnect, serverTimestamp, DatabaseReference } from 'firebase/database';
import { doc, updateDoc, increment as firestoreIncrement } from 'firebase/firestore';
import { useUserPresenceStore } from '../store/userPresenceStore';
import { AppState, AppStateStatus } from 'react-native';

class UserPresenceService {
  private db = firebaseService.getDb();
  private rtdb = firebaseService.getRTDB();
  private userStatusDatabaseRef: DatabaseReference | null = null;
  private isSetup = false;
  private appStateSubscription: any = null;

  async setupPresence() {
    if (this.isSetup) return;

    const user = firebaseService.getCurrentUser();
    if (!user) return;

    // Create references
    const uid = user.uid;
    const userStatusFirestoreRef = doc(this.db, 'user-status', 'stats');
    this.userStatusDatabaseRef = ref(this.rtdb, `/status/${uid}`);
    const connectedRef = ref(this.rtdb, '.info/connected');

    // Handle app state changes
    this.appStateSubscription = AppState.addEventListener('change', (nextAppState: AppStateStatus) => {
      this.updateOnlineStatus(nextAppState === 'active');
    });

    // Listen for connection state changes
    onValue(connectedRef, async (snapshot) => {
      // If we're not connected to Firebase, don't do anything
      if (snapshot.val() === false) {
        return;
      }

      // Set up presence handling
      if (this.userStatusDatabaseRef) {
        try {
          // When user disconnects
          await onDisconnect(this.userStatusDatabaseRef).set({
            state: 'offline',
            lastChanged: serverTimestamp(),
          });

          // Check current app state
          const currentAppState = AppState.currentState;
          const isOnline = currentAppState === 'active';

          // Set initial state
          await set(this.userStatusDatabaseRef, {
            state: isOnline ? 'online' : 'offline',
            lastChanged: serverTimestamp(),
          });

          // Update stats in Firestore
          if (isOnline) {
            await updateDoc(userStatusFirestoreRef, {
              onlineCount: firestoreIncrement(1)
            });
          }

          // Setup disconnect handler for stats
          onDisconnect(this.userStatusDatabaseRef).update({
            state: 'offline',
            lastChanged: serverTimestamp(),
          }).then(async () => {
            await updateDoc(userStatusFirestoreRef, {
              onlineCount: firestoreIncrement(-1)
            });
          });

          console.log('Presence setup complete for user:', uid);
        } catch (error) {
          console.error('Error setting up presence:', error);
        }
      }
    });

    // Start listening to presence stats
    this.subscribeToPresenceStats();

    this.isSetup = true;
    console.log('Presence service initialized');
  }

  private async updateOnlineStatus(isOnline: boolean) {
    const user = firebaseService.getCurrentUser();
    if (!user || !this.userStatusDatabaseRef) return;

    try {
      console.log('Updating online status:', isOnline);
      await set(this.userStatusDatabaseRef, {
        state: isOnline ? 'online' : 'offline',
        lastChanged: serverTimestamp(),
      });

      // Update stats in Firestore
      const userStatusFirestoreRef = doc(this.db, 'user-status', 'stats');
      await updateDoc(userStatusFirestoreRef, {
        onlineCount: firestoreIncrement(isOnline ? 1 : -1)
      });
    } catch (error) {
      console.error('Error updating online status:', error);
    }
  }

  subscribeToPresenceStats() {
    const statusRef = ref(this.rtdb, '/status');
    return onValue(statusRef, (snapshot) => {
      const users = snapshot.val() || {};
      const onlineCount = Object.values(users)
        .filter((status: any) => status.state === 'online')
        .length;
      const totalUsers = Object.keys(users).length;

      console.log('Presence stats updated:', { onlineCount, totalUsers });
      useUserPresenceStore.getState().setOnlineCount(onlineCount);
      useUserPresenceStore.getState().setTotalUsers(totalUsers);
    });
  }

  cleanup() {
    if (this.appStateSubscription) {
      this.appStateSubscription.remove();
      this.appStateSubscription = null;
    }
    this.isSetup = false;
    console.log('Presence service cleaned up');
  }
}

export const userPresenceService = new UserPresenceService();
